import {
    AbstractDataStore,
    DataStoreGetResult,
    type DataStoreName,
    StorageType,
} from '@core/storage/abstract-data-store.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { Env } from '../../env.ts'
import { Result } from '@core/models/result.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { Globals } from '@core/globals.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'

/**
 * @internal
 */
export const SessionDataStoreException = {
    notAvailable: () => new PNException(PNExceptionCode.STORAGE_NOT_AVAILABLE, null, ' - Session'),
    noDataFor: (key: string) => new PNException(PNExceptionCode.STORAGE_KEY_EMPTY, null, key),
    unableToOpen: (reason: Error) => new PNException(PNExceptionCode.STORAGE_FAILED_TO_OPEN, reason, ` - Session`),
} as const

/**
 * @internal
 */
export class SessionDataStore extends AbstractDataStore {
    /**
     * sessionStorage key this store reads and writes.
     *
     * Defaults to the store name, which is also the IndexedDB object store name. It can be
     * overridden so two managers sharing an IndexedDB store do not have to share one
     * sessionStorage blob: every read and write here re-serialises the whole blob, so an
     * unrelated manager writing a large value makes every other manager's access more
     * expensive. Overriding does not affect IndexedDB, and deliberately is not a
     * `DataStoreName` - adding one of those would make `IdbDataStore._selfHeal` look for an
     * object store that `_handleOnUpgradeNeeded` never creates.
     */
    private readonly _namespace: string | null

    public constructor(
        storeName: DataStoreName | null,
        logger: Logger = TaggedLogger._getInstance('SessionStore'),
        namespace?: string,
    ) {
        super(StorageType.SESSION, storeName, logger)

        this._namespace = namespace ?? storeName
    }

    public get _isAvailable(): boolean {
        return !!this._namespace && !this._isInWorkerGlobalScope && this.storageAvailable()
    }

    protected get _store(): Storage {
        return Env._scope.sessionStorage
    }

    public async _getData<T>(key: string): Promise<DataStoreGetResult<T>> {
        if (!this._isAvailable) {
            return Result.Err(SessionDataStoreException.notAvailable())
        }

        const openRes = this._openStore()
        if (openRes.isErr()) {
            return openRes
        }

        const db = openRes.unwrap()
        const value = db[key] || null

        if ((value === undefined || value === null) && key !== Globals._pnStorageTypeLuaKey) {
            return Result.Err(SessionDataStoreException.noDataFor(key))
        }

        return Result.Ok(value)
    }

    public async _setData<T>(key: string, value: T): Promise<Result<T>> {
        if (!this._isAvailable) {
            return Result.Err(SessionDataStoreException.notAvailable())
        }

        const openRes = this._openStore()
        if (openRes.isErr()) {
            return openRes
        }

        const db = openRes.unwrap()

        if (key === Globals._pnStorageTypeLuaKey) {
            db[key] = value
        } else {
            db[key] = { value, ts: new Date().getTime() }
        }

        this._persist(db)

        return Result.Ok(value)
    }

    public async _removeData(key: string): Promise<Result<null>> {
        const openRes = this._openStore()
        if (openRes.isErr()) {
            return openRes
        }

        // Keys live directly on the opened object, the same shape `_setData` writes. This
        // previously looked for them one level down under the store name, which `_openStore`
        // never produces, so no key was ever actually removed.
        const db = openRes.unwrap()
        if (key in db) {
            delete db[key]

            this._persist(db)
        }

        return Result.Ok(null)
    }

    protected _openStore(): Result<Record<string, any>> {
        let db: Record<string, any> = {}

        if (!this._namespace) {
            return Result.Err(
                SessionDataStoreException.unableToOpen(new Error('Store cannot be accessed without a store name set')),
            )
        }

        try {
            const storeValue = this._store.getItem(this._namespace) ?? '{}'
            const _db = JSON.parse(storeValue)
            if (_db && typeof _db === 'object' && !Array.isArray(_db)) {
                db = _db
            }
        } catch (err) {
            const error = SessionDataStoreException.unableToOpen(ensureError(err))
            this._logger.warn(error.message)

            return Result.Err(error)
        }

        return Result.Ok(db)
    }

    protected _persist(dbUpdate: any) {
        try {
            if (dbUpdate && typeof dbUpdate === 'object' && !Array.isArray(dbUpdate)) {
                dbUpdate[Globals._pnStorageTypeLuaKey] = new Date().getTime()
            }

            this._store.setItem(this._namespace!, JSON.stringify(dbUpdate))
        } catch (err) {
            this._logger.error(ensureError(err))
        }
    }

    /**
     * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability)
     */
    protected storageAvailable(): boolean {
        let storage: Storage | undefined
        try {
            storage = this._store
            const x = Globals._pnSessionStorageKey + '_test_'

            storage.setItem(x, x)
            storage.removeItem(x)

            return true
        } catch (e) {
            return (
                e instanceof DOMException &&
                e.name === 'QuotaExceededError' &&
                // acknowledge QuotaExceededError only if there's something already stored
                !!storage &&
                storage.length !== 0
            )
        }
    }
}
